import React, { useEffect, useMemo, useRef, useState } from "react";
import { FiSend } from "react-icons/fi";
import BackendHeader from "../component/BackendHeader";
import axiosInstance from "../axiosInstance";
import { fetchChatPermissions } from "../libs/customerCreatePermission";
import {
  getAdminChatMessages,
  getAdminChatSession,
  markAdminChatRead,
  sendAdminChatMessage,
} from "../chatAdminApi";
import { buildAdminChatWebSocketUrl } from "../chatAdminApi";
import "../css/adminchat.css";

const MESSAGE_PAGE_SIZE = 30;

const formatDateTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return date.toLocaleString("vi-VN", {
    timeZone: "Asia/Ho_Chi_Minh",
    hour: "2-digit",
    minute: "2-digit",
    day: "2-digit",
    month: "2-digit",
  });
};

const sortConversations = (items = []) =>
  [...items].sort((left, right) => {
    const leftTime = left?.lastMessageAt ? new Date(left.lastMessageAt).getTime() : 0;
    const rightTime = right?.lastMessageAt
      ? new Date(right.lastMessageAt).getTime()
      : 0;
    return rightTime - leftTime || (right.id || 0) - (left.id || 0);
  });

const sortMessages = (items = []) =>
  [...items].sort((left, right) => {
    const leftTime = left?.createdAt ? new Date(left.createdAt).getTime() : 0;
    const rightTime = right?.createdAt ? new Date(right.createdAt).getTime() : 0;
    return leftTime - rightTime || (left.id || 0) - (right.id || 0);
  });

const mergeMessages = (currentMessages = [], incomingMessages = []) => {
  const mergedById = new Map();

  [...currentMessages, ...incomingMessages].forEach((message) => {
    const stableKey = message?.id || message?.senderType + "-" + message?.createdAt;
    mergedById.set(stableKey, message);
  });

  return sortMessages(Array.from(mergedById.values()));
};

const upsertConversation = (items, updatedConversation) => {
  const nextItems = [...items];
  const index = nextItems.findIndex(
    (item) => item.publicToken === updatedConversation.publicToken
  );

  if (index >= 0) {
    nextItems[index] = { ...nextItems[index], ...updatedConversation };
  } else {
    nextItems.unshift(updatedConversation);
  }

  return sortConversations(nextItems);
};

const AdminChatInbox = () => {
  const [conversations, setConversations] = useState([]);
  const [selectedPublicToken, setSelectedPublicToken] = useState("");
  const [selectedSession, setSelectedSession] = useState(null);
  const [draftMessage, setDraftMessage] = useState("");
  const [isLoadingSession, setIsLoadingSession] = useState(false);
  const [isLoadingOlderMessages, setIsLoadingOlderMessages] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [draftEmptyWarning, setDraftEmptyWarning] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState("dang-ket-noi");
  const [errorMessage, setErrorMessage] = useState("");
  const [chatPerm, setChatPerm] = useState({ loaded: false, canView: false });
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [nextBeforeMessageId, setNextBeforeMessageId] = useState(null);

  const socketRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pollingIntervalRef = useRef(null);
  const selectedPublicTokenRef = useRef(selectedPublicToken);
  const selectedSessionRef = useRef(selectedSession);
  const messageViewportRef = useRef(null);
  const markReadTimeoutRef = useRef(null);

  useEffect(() => {
    selectedPublicTokenRef.current = selectedPublicToken;
    window.dispatchEvent(
      new CustomEvent("admin-chat-active-conversation", {
        detail: { publicToken: selectedPublicToken || "" },
      })
    );

    return () => {
      window.dispatchEvent(
        new CustomEvent("admin-chat-active-conversation", {
          detail: { publicToken: "" },
        })
      );
    };
  }, [selectedPublicToken]);

  useEffect(() => {
    selectedSessionRef.current = selectedSession;
  }, [selectedSession]);

  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent("admin-chat-conversations-sync", {
        detail: { conversations, connectionStatus },
      })
    );
  }, [conversations, connectionStatus]);

  useEffect(() => {
    let cancelled = false;
    const loadPerm = async () => {
      const p = await fetchChatPermissions(axiosInstance);
      if (!cancelled) {
        setChatPerm({ loaded: true, canView: Boolean(p?.canView) });
      }
    };
    loadPerm();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!chatPerm.loaded || !chatPerm.canView) return;
    connectSocket();

    return () => {
      window.clearTimeout(reconnectTimeoutRef.current);
      window.clearInterval(pollingIntervalRef.current);
      window.clearTimeout(markReadTimeoutRef.current);
      socketRef.current?.close();
    };
  }, [chatPerm.loaded, chatPerm.canView]);

  useEffect(() => {
    if (!selectedPublicToken) return;
    loadSession(selectedPublicToken);
  }, [selectedPublicToken]);

  useEffect(() => {
    const viewport = messageViewportRef.current;
    if (!viewport) return;
    viewport.scrollTop = viewport.scrollHeight;
  }, [selectedSession]);

  const selectedConversation = useMemo(
    () =>
      conversations.find(
        (conversation) => conversation.publicToken === selectedPublicToken
      ) || null,
    [conversations, selectedPublicToken]
  );

  const connectSocket = () => {
    window.clearTimeout(reconnectTimeoutRef.current);
    socketRef.current?.close();

    try {
      const socket = new WebSocket(buildAdminChatWebSocketUrl());
      socketRef.current = socket;
      setConnectionStatus("dang-ket-noi");

      socket.onopen = () => {
        setConnectionStatus("truc-tuyen");
        startPolling(false);
        socket.send(JSON.stringify({
          type: "subscribe_admin",
          accessToken: localStorage.getItem("token") || "",
        }));
      };

      socket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          handleSocketEvent(data);
        } catch (error) {
          console.error("Invalid admin chat socket message", error);
        }
      };

      socket.onerror = () => {
        setConnectionStatus("mat-ket-noi");
        startPolling(true);
      };

      socket.onclose = () => {
        if (socketRef.current === socket) {
          socketRef.current = null;
        }
        setConnectionStatus("mat-ket-noi");
        startPolling(true);
        reconnectTimeoutRef.current = window.setTimeout(() => {
          connectSocket();
        }, 4000);
      };
    } catch (error) {
      setConnectionStatus("mat-ket-noi");
      startPolling(true);
    }
  };

  const startPolling = (shouldStart) => {
    window.clearInterval(pollingIntervalRef.current);
    if (!shouldStart) return;

    pollingIntervalRef.current = window.setInterval(async () => {
      if (selectedPublicTokenRef.current) {
        await loadSession(selectedPublicTokenRef.current, false);
      }
    }, 5000);
  };

  const scheduleMarkAdminRead = (publicToken) => {
    if (!publicToken) return;

    window.clearTimeout(markReadTimeoutRef.current);
    markReadTimeoutRef.current = window.setTimeout(() => {
      markAdminChatRead(publicToken)
        .then(() => {
          setConversations((currentConversations) =>
            currentConversations.map((conversation) =>
              conversation.publicToken === publicToken
                ? { ...conversation, unreadAdminCount: 0 }
                : conversation
            )
          );

          setSelectedSession((currentSession) =>
            currentSession?.conversation?.publicToken === publicToken
              ? {
                  ...currentSession,
                  conversation: {
                    ...currentSession.conversation,
                    unreadAdminCount: 0,
                  },
                }
              : currentSession
          );

          window.dispatchEvent(
            new CustomEvent("admin-chat-read", { detail: { publicToken } })
          );
        })
        .catch((error) => {
          console.error("Cannot mark admin chat read", error);
        });
    }, 250);
  };

  const loadSession = async (publicToken, showLoader = true) => {
    if (!publicToken) return;
    if (showLoader) {
      setIsLoadingSession(true);
    }

    try {
      const response = await getAdminChatSession(publicToken, MESSAGE_PAGE_SIZE);
      if (!response?.success || !response?.data) {
        throw new Error(response?.message || "Khong the tai hoi thoai");
      }

      setSelectedSession((currentSession) => {
        const currentMessages =
          currentSession?.conversation?.publicToken === publicToken
            ? currentSession.messages || []
            : [];

        return {
          ...response.data,
          messages: mergeMessages(currentMessages, response.data.messages || []),
        };
      });
      setHasMoreMessages(Boolean(response.data.hasMoreMessages));
      setNextBeforeMessageId(response.data.nextBeforeMessageId || null);
      setConversations((currentConversations) =>
        upsertConversation(currentConversations, {
          ...response.data.conversation,
          unreadAdminCount: 0,
        })
      );
      scheduleMarkAdminRead(publicToken);
    } catch (error) {
      setErrorMessage(
        error?.response?.data?.message ||
          error?.message ||
          "Không thể tải nội dung hội thoại"
      );
    } finally {
      if (showLoader) {
        setIsLoadingSession(false);
      }
    }
  };

  const loadOlderMessages = async () => {
    if (!selectedPublicToken || !nextBeforeMessageId || isLoadingOlderMessages) {
      return;
    }

    setIsLoadingOlderMessages(true);

    try {
      const response = await getAdminChatMessages(
        selectedPublicToken,
        nextBeforeMessageId,
        MESSAGE_PAGE_SIZE
      );
      if (!response?.success || !response?.data) {
        throw new Error(response?.message || "Khong the tai tin nhan cu");
      }

      setSelectedSession((currentSession) =>
        currentSession
          ? {
              ...currentSession,
              messages: mergeMessages(response.data.messages || [], currentSession.messages || []),
            }
          : currentSession
      );
      setHasMoreMessages(Boolean(response.data.hasMore));
      setNextBeforeMessageId(response.data.nextBeforeMessageId || null);
    } catch (error) {
      setErrorMessage(
        error?.response?.data?.message ||
          error?.message ||
          "Không thể tải thêm tin nhắn cũ"
      );
    } finally {
      setIsLoadingOlderMessages(false);
    }
  };

  const handleSocketEvent = (event) => {
    if (!event?.type || event.type === "pong" || event.type === "subscribed") {
      return;
    }

    if (event.type === "error") {
      setErrorMessage(event.payload || "Kết nối realtime gặp lỗi");
      return;
    }

    if (event.type === "chat.conversation.updated" && event.payload) {
      const isActiveConversation =
        event.payload.publicToken === selectedPublicTokenRef.current;
      const updatedConversation = isActiveConversation
        ? { ...event.payload, unreadAdminCount: 0 }
        : event.payload;

      setConversations((currentConversations) =>
        upsertConversation(currentConversations, updatedConversation)
      );

      if (isActiveConversation) {
        setSelectedSession((currentSession) =>
          currentSession
            ? {
                ...currentSession,
                conversation: { ...currentSession.conversation, ...updatedConversation },
              }
            : currentSession
        );

        if ((event.payload.unreadAdminCount || 0) > 0) {
          scheduleMarkAdminRead(event.payload.publicToken);
        }
      }
      return;
    }

    if (event.type === "chat.message" && event.payload) {
      const currentConversationId = selectedSessionRef.current?.conversation?.id;
      if (currentConversationId !== event.payload.conversationId) {
        return;
      }

      setSelectedSession((currentSession) => {
        if (!currentSession) return currentSession;

        return {
          ...currentSession,
          messages: mergeMessages(currentSession.messages || [], [event.payload]),
        };
      });

      if (event.payload.senderType === "visitor") {
        scheduleMarkAdminRead(selectedPublicTokenRef.current);
      }
    }
  };

  const handleSendMessage = async (event) => {
    event.preventDefault();
    const content = draftMessage.trim();
    if (!content) {
      setDraftEmptyWarning(true);
      return;
    }
    setDraftEmptyWarning(false);
    if (!selectedPublicToken || isSending) return;

    setIsSending(true);
    setErrorMessage("");

    try {
      const response = await sendAdminChatMessage({
        publicToken: selectedPublicToken,
        senderName: "Admin",
        content,
      });

      if (!response?.success || !response?.data) {
        throw new Error(response?.message || "Gửi tin nhắn thất bại");
      }

      setSelectedSession((currentSession) => {
        if (!currentSession) return currentSession;

        return {
          ...currentSession,
          messages: mergeMessages(currentSession.messages || [], [response.data]),
        };
      });
      setDraftMessage("");
    } catch (error) {
      setErrorMessage(
        error?.response?.data?.message ||
          error?.message ||
          "Không thể gửi tin nhắn"
      );
    } finally {
      setIsSending(false);
    }
  };

  const statusLabel =
    connectionStatus === "truc-tuyen"
      ? "Realtime online"
      : connectionStatus === "dang-ket-noi"
      ? "Đang kết nối"
      : "Đang tải lại tin nhắn";

  return (
    <div>
      <BackendHeader />
      {chatPerm.loaded && !chatPerm.canView ? (
        <div className="container-fluid py-4 mt-5">
          <div className="alert alert-warning mb-0">Bạn không có quyền truy cập màn hình này.</div>
        </div>
      ) : (
      <div className="container-fluid py-4 mt-5">
        <div className="row g-3">
          <div className="col-12">
            <div className="card shadow-sm border-0">
              <div className="card-body d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-2">
                <div>
                  <h4 className="mb-1">Admin Chat Inbox</h4>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <span className="badge text-bg-primary">{statusLabel}</span>
                </div>
              </div>
            </div>
          </div>

          {errorMessage && (
            <div className="col-12">
              <div className="alert alert-warning mb-0" role="alert">
                {errorMessage}
              </div>
            </div>
          )}

          <div className="col-12 col-xl-4">
            <div className="card shadow-sm border-0 h-100">
              <div className="card-header bg-white fw-semibold">
                Hội thoại đang mở
              </div>
              <div
                className="list-group list-group-flush"
                style={{ maxHeight: "70vh", overflowY: "auto" }}
              >
                {conversations.length === 0 ? (
                  <div className="p-3 text-muted">Chưa có hội thoại nào.</div>
                ) : (
                  conversations.map((conversation) => (
                    <button
                      key={conversation.publicToken}
                      type="button"
                      className={
                        "list-group-item list-group-item-action text-start " +
                        (selectedPublicToken === conversation.publicToken ? "active" : "")
                      }
                      onClick={() => setSelectedPublicToken(conversation.publicToken)}
                    >
                      <div className="d-flex justify-content-between align-items-start gap-2">
                        <div className="flex-grow-1">
                          <div className="fw-semibold">
                            {conversation.visitorName || "Khách chưa đặt tên"}
                          </div>
                          <div
                            className={
                              "small " +
                              (selectedPublicToken === conversation.publicToken
                                ? "text-white-50"
                                : "text-muted")
                            }
                          >
                            {conversation.lastMessagePreview || "Chua co tin nhan"}
                          </div>
                        </div>
                        {conversation.unreadAdminCount > 0 && (
                          <span className="badge rounded-pill text-bg-danger">
                            {conversation.unreadAdminCount}
                          </span>
                        )}
                      </div>
                      <div
                        className={
                          "small mt-2 " +
                          (selectedPublicToken === conversation.publicToken
                            ? "text-white-50"
                            : "text-muted")
                        }
                      >
                        {formatDateTime(conversation.lastMessageAt)}
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>
          </div>

          <div className="col-12 col-xl-8">
            <div className="card shadow-sm border-0">
              <div className="card-header bg-white">
                <div className="d-flex justify-content-between align-items-center gap-2">
                  <div>
                    <div className="fw-semibold">
                      {selectedConversation?.visitorName || "Chọn một hội thoại"}
                    </div>
                    <div className="small text-muted">
                      {selectedConversation?.visitorPhone ||
                        selectedConversation?.visitorEmail ||
                        selectedConversation?.publicToken ||
                        "Thong tin khach se hien thi tai day"}
                    </div>
                  </div>
                  {selectedConversation?.status && (
                    <span className="badge text-bg-light border text-dark">
                      {selectedConversation.status}
                    </span>
                  )}
                </div>
              </div>

              <div
                className="card-body bg-light"
                ref={messageViewportRef}
                style={{ minHeight: "52vh", maxHeight: "52vh", overflowY: "auto" }}
              >
                {!selectedPublicToken ? (
                  <div className="text-muted">Chọn hội thoại để xem chi tiết.</div>
                ) : isLoadingSession ? (
                  <div className="text-muted">Đang tải nội dung hội thoại...</div>
                ) : !selectedSession?.messages?.length ? (
                  <div className="text-muted">Hội thoại chưa có tin nhắn.</div>
                ) : (
                  <>
                    {(hasMoreMessages || isLoadingOlderMessages) && (
                      <div className="d-flex justify-content-center mb-3">
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm"
                          onClick={loadOlderMessages}
                          disabled={isLoadingOlderMessages}
                        >
                          {isLoadingOlderMessages ? "Dang tai..." : "Tai tin nhan cu hon"}
                        </button>
                      </div>
                    )}
                    {selectedSession.messages.map((message) => {
                      const isAdmin = message.senderType === "admin";
                      const isVisitor = message.senderType === "visitor";
                      const bubbleClass = isAdmin
                        ? "bg-primary text-white ms-auto"
                        : isVisitor
                        ? "bg-white border"
                        : "bg-warning-subtle border";

                      return (
                        <div
                          key={message.id || message.senderType + "-" + message.createdAt}
                          className={
                            "d-flex mb-3 " +
                            (isAdmin ? "justify-content-end" : "justify-content-start")
                          }
                        >
                          <div
                            className={"rounded-4 px-3 py-2 shadow-sm " + bubbleClass}
                            style={{ maxWidth: "75%" }}
                          >
                            <div className="small fw-semibold mb-1">
                              {isAdmin ? "Admin" : message.senderName || "Khach"}
                            </div>
                            <div style={{ whiteSpace: "pre-wrap" }}>{message.content}</div>
                            <div
                              className={
                                "small mt-2 " +
                                (isAdmin ? "text-white-50" : "text-muted")
                              }
                            >
                              {formatDateTime(message.createdAt)}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </>
                )}
              </div>

              <div className="card-footer bg-white">
                <form className="d-flex gap-2" onSubmit={handleSendMessage}>
                  <input
                    type="text"
                    className={
                      "form-control " +
                      (draftEmptyWarning ? "admin-chat-inbox-input--empty" : "")
                    }
                    value={draftMessage}
                    onChange={(event) => {
                      setDraftMessage(event.target.value);
                      if (draftEmptyWarning && event.target.value.trim()) {
                        setDraftEmptyWarning(false);
                      }
                    }}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        handleSendMessage(event);
                      }
                    }}
                    placeholder="Nhập phản hồi cho khách..."
                    disabled={!selectedPublicToken || isSending}
                  />
                  <button
                    type="submit"
                    className="btn btn-primary px-4 d-inline-flex align-items-center gap-2"
                  >
                    <FiSend aria-hidden="true" />
                    {isSending ? "Dang gui" : "Gửi"}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
      )}
    </div>
  );
};

export default AdminChatInbox;
